import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';

export type AiAgentKeyRole = 'AGENT' | 'HOST';
export type AiAgentKeyStatus = 'ACTIVE' | 'REVOKED' | 'EXPIRED';
/** API_KEY: caller holds a secret. KEYCLOAK_CLIENT: caller logs in with its own Keycloak client; no secret here. */
export type AiAgentKeyKind = 'API_KEY' | 'KEYCLOAK_CLIENT';
/** Chat channel a caller is bound to; 'admin' is this screen's own caller. */
export type AiAgentChannel = 'ecommerce' | 'backoffice' | 'pos' | 'admin';

export interface AiAgentKey {
  keyId: string;
  kind: AiAgentKeyKind;
  channel: AiAgentChannel;
  role: AiAgentKeyRole;
  status: AiAgentKeyStatus;
  createdAt: string;
  createdBy: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
}

/** Only returned by create - the secret cannot be fetched again afterwards. */
export interface CreatedAiAgentKey {
  keyId: string;
  kind: AiAgentKeyKind;
  channel: AiAgentChannel;
  secret: string | null;
  role: AiAgentKeyRole;
  createdAt: string;
  expiresAt: string | null;
}

@Injectable({ providedIn: 'root' })
export class AiAgentKeysService {
  private readonly http = inject(HttpClient);
  private readonly basePath = `${environment.aiAgentChatBasePath}/v1/agent-keys`;

  list(): Observable<AiAgentKey[]> {
    return this.http.get<AiAgentKey[]>(this.basePath);
  }

  /** Creates an API key bound to a chat channel; the response carries its secret once. */
  createApiKey(keyId: string, channel: AiAgentChannel): Observable<CreatedAiAgentKey> {
    return this.http.post<CreatedAiAgentKey>(this.basePath, { kind: 'API_KEY', keyId, channel });
  }

  /** API key: permanent. Keycloak client: disabled until activated. */
  revoke(keyId: string): Observable<AiAgentKey> {
    return this.http.post<AiAgentKey>(`${this.basePath}/${encodeURIComponent(keyId)}/revoke`, {});
  }

  /** Keycloak clients only. */
  activate(keyId: string): Observable<AiAgentKey> {
    return this.http.post<AiAgentKey>(`${this.basePath}/${encodeURIComponent(keyId)}/activate`, {});
  }
}
